<template>
  <Loading v-if="isLoading" loadingString="Initialize Pin" />
  <div
    v-else
    id="mainDiv"
    class="container roundedBorder secondary-bg primary-fg"
  >
    <h2>Initialize Pin (CSE)</h2>

    <div class="row">
      <div class="input-field col s8 m9">
        <i class="material-icons prefix secondary-fg">lock_outline</i>
        <input
          id="enteredPinField"
          class="primary-fg"
          type="text"
          v-model="enteredPin"
        />
        <label for="enteredPinField">PIN</label>
      </div>
      <div class="col s4 m3" style="padding-top: 20px">
        <a
          v-on:click="initializePin"
          class="waves-effect waves-light primary-bg primary-fg btn s12"
          style="width: 100%"
        >
          Submit
          <i class="material-icons left">check</i>
        </a>
      </div>
    </div>

    <span class="primary-fg">
      Client-side encryption (CSE) is a system that allows you to securely hide
      your data even from the LockerApp administrator. It is an optional feature
      for users that wants to introduce better security. After initializing a
      PIN, all of your data will be encryption on the client-side (on your own
      device) and it won't be decryptable by LockerApp.
    </span>
    <p></p>
    <span class="primary-fg">
      It would be a good idea to backup your passwords before enabling client
      side encrpytion. You can export your passwords by navigating to the
      <router-link to="/settings">settings</router-link>
      .
    </span>
    <p></p>
    <span class="error-fg">
      After initializing a PIN, your PIN (client-side encryption key) and your
      unencrypted data are never sent to LockerApp. It's important that you
      safely manage your encryption keys. If you lose them, you can't decrypt
      your data.
    </span>
  </div>
</template>

<script>
import M from "materialize-css";
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/database";
import Loading from "../components/Loading.vue";
import { SecretPasswordObject, PIN_INIT_API_ENDPOINT } from "../common.js";

export default {
  name: "InitializePin",
  components: {
    Loading
  },
  data: function() {
    return {
      enteredPin: "",
      isLoading: true
    };
  },
  methods: {
    pinValidation: async function() {
      // Check if pin is already initialized
      let cseEnabled = await this.isCSEEnabled();

      if (cseEnabled) {
        this.$router.push("/pin/update");
      }

      this.isLoading = false;
    },
    initializePin: async function() {
      let enteredPin = this.enteredPin;
      if (enteredPin.length < 4) {
        alert("The PIN should be at least 4 characters");
        return;
      }

      this.isLoading = true;

      // Create a salt
      let salt = this.generateSalt();

      // Create the hash of (pin + salt)
      let hops = this.calculateHash(enteredPin, salt);

      // Create the hash of hash of (pin + salt)
      let hohoPin = this.calculateHash(hops, salt);

      // Fetch the data to be encrypted on the client side
      let passwords = await this.fetchPasswords();
      let notes = await this.fetchNotes();

      // Re-ecrypt all data on the client side
      for (let i = 0; i < passwords.length; i++) {
        passwords[i] = new SecretPasswordObject(
          passwords[i].guid,
          this.encrypt(passwords[i].content, hops),
          this.encrypt(passwords[i].username, hops),
          this.encrypt(passwords[i].password, hops),
          this.encrypt(passwords[i].website, hops),
          this.encrypt(passwords[i].isFavorite, hops),
          this.encrypt(passwords[i].notes, hops)
        );
      }

      for (let i = 0; i < notes.length; i++) {
        notes[i] = {
          noteContent: this.encrypt(notes[i].noteContent, hops),
          lastEditedDate: this.encrypt(notes[i].lastEditedDate, hops),
          guid: notes[i].guid,
          timeStamp: this.encrypt(notes[i].timeStamp, hops),
          isFavorite: this.encrypt(notes[i].isFavorite, hops)
        };
      }

      // Store the hohoPin and salt at the database
      let idToken = await this.getIdToken();
      const options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken
        },
        body: JSON.stringify({
          salt: salt,
          hohoPin: hohoPin,
          encryptedPasswords: passwords,
          encryptedNotes: notes,
        })
      };
      let serverResponse = await fetch(PIN_INIT_API_ENDPOINT, options)
        .then(res => {
          return res.json();
        })
        .catch(err => {
          this.isLoading = false;
          alert(`Failed initializing the PIN: ${err}`);
          return;
        });

      if (!serverResponse.success) {
        this.isLoading = false;
        alert(`Failed initalizing the PIN. ${serverResponse.data}`);
        return;
      }

      // Save the hops in the local storage
      this.setHops(hops);

      this.isLoading = false;
      alert("PIN encryption is successfully initialized");
      this.$router.push("/");
    }
  },
  mounted: function() {
    // If there is no user navigate to the root page
    if (!firebase.auth().currentUser) {
      this.$router.push("/");
      return;
    }

    // Initialize the materialize components
    // This also closes the navbar if open
    M.AutoInit();

    this.pinValidation();
  },
  metaInfo: {
    title: "Initialize Pin",
    meta: [
      {
        name: "description",
        content: "Store encypted passwords with LockerApp."
      },
      { name: "application-name", content: "LockerApp" },
      { name: "author", content: "Emre Biçer" },
      {
        name: "keywords",
        content:
          "LockerApp, Locker App, Locker, App, Password Manager, Password, Safe, Application, Last Password, Locker, Notes, Note Application, Mobile Application, Web Application"
      }
    ]
  }
};
</script>

<style scoped>
#mainDiv {
  padding: 20px;
}

.loading {
  background-image: url("../assets/loading.gif");
  background-size: 100%;
}
</style>
